import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { TrendingUp, DollarSign, CheckCircle, AlertTriangle, RefreshCw, ExternalLink, CalendarClock } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  useDashboardSummary,
  useUpcomingRenewals,
  useSpendTrends,
  useContractsByStatus,
  useLicenseUtilization,
  useSpendByVendor,
  useSpendByDepartment,
  useUpcomingMilestones,
  useCostPerUser,
} from '../services/dashboardService';
import { queryKeys } from '../services/queryClient';
import { formatCurrency, formatCalendarDate } from '../utils/formatters';
import { formatCostPerUser } from '../utils/costPerUser';
import { ContractStatus, contractDateTypeLabel } from '../types';
import type { UpcomingRenewal, UpcomingMilestone, CostPerUserItem } from '../types';
import { RenewalForecastCard } from '@/components/dashboard/RenewalForecastCard';
import { BudgetSummaryCard } from '@/components/dashboard/BudgetSummaryCard';
import { DocumentReviewsCard } from '@/components/dashboard/DocumentReviewsCard';

const PIE_COLORS = ['#22C55E', '#FF5630', '#FFAB00', '#919EAB', '#00B8D9'];

/**
 * Summary card component props
 */
interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  isLoading?: boolean;
  /** When set, the whole card links to the contract list filtered to this status */
  linkToStatus?: ContractStatus;
  testId?: string;
}

/**
 * SummaryCard Component
 *
 * Displays a key metric with an icon, title, value, and optional subtitle.
 */
const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  icon,
  color,
  subtitle,
  isLoading,
  linkToStatus,
  testId,
}) => {
  const card = (
    <Card className={cn('h-full flex flex-col', linkToStatus && 'transition-colors hover:bg-grey-50')} data-testid={testId}>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center w-14 h-14 rounded-lg shrink-0 text-white"
            style={{ backgroundColor: color }}
          >
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-grey-500 mb-1">{title}</p>
            {isLoading ? (
              <Skeleton className="h-9 w-3/5" />
            ) : (
              <p className="text-2xl font-bold text-grey-800">{value}</p>
            )}
            {subtitle && (
              <p className="text-xs text-grey-500 mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (!linkToStatus) {
    return card;
  }

  return (
    <Link
      to="/contracts"
      search={{ status: linkToStatus }}
      className="block h-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={`${title}: open the contract list filtered to ${linkToStatus}`}
    >
      {card}
    </Link>
  );
};

/**
 * Badge variant for a "days until" count: red under 30, amber under 60, blue otherwise.
 */
const getUrgencyVariant = (days: number): 'destructive' | 'warning' | 'info' => {
  if (days < 30) return 'destructive';
  if (days < 60) return 'warning';
  return 'info';
};

/**
 * RenewalRow Component
 *
 * Displays a single upcoming renewal with color coding based on urgency.
 */
const RenewalRow: React.FC<{
  renewal: UpcomingRenewal;
  onClick: (contractId: string) => void;
}> = ({ renewal, onClick }) => {
  return (
    <div
      className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 cursor-pointer hover:bg-grey-50 transition-colors"
      onClick={() => onClick(renewal.contractId)}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-semibold text-grey-800 truncate">
            {renewal.contractName}
          </span>
          <button className="ml-auto shrink-0 text-grey-400 hover:text-grey-600">
            <ExternalLink className="h-4 w-4" />
          </button>
        </div>
        <span className="text-sm text-grey-500 truncate block">{renewal.vendorName}</span>
      </div>
      <div className="text-right min-w-[120px]">
        <span className="text-sm text-grey-500 block">{formatCalendarDate(renewal.endDate)}</span>
        <Badge variant={getUrgencyVariant(renewal.daysUntil)} className="mt-1">
          {renewal.daysUntil} days
        </Badge>
      </div>
      <div className="text-right min-w-[120px]">
        <span className="text-sm font-semibold text-grey-800 block">
          {formatCurrency(renewal.annualContractValue)}
        </span>
        {renewal.autoRenews && (
          <span className="text-xs text-grey-500">Auto-renews</span>
        )}
      </div>
    </div>
  );
};

/**
 * MilestoneRow Component
 *
 * Displays a single upcoming contract milestone (ContractDate) with its type,
 * contract, vendor, calendar date and a days-until badge colored by urgency.
 */
const MilestoneRow: React.FC<{
  milestone: UpcomingMilestone;
  onClick: (contractId: string) => void;
}> = ({ milestone, onClick }) => {
  const open = () => onClick(milestone.contractId);
  return (
    // The whole row is the control: focusable, labelled, and activated by Enter or Space as
    // well as by pointer, so keyboard users can open the contract too.
    <div
      role="link"
      tabIndex={0}
      aria-label={`Open ${milestone.contractTitle}`}
      className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 cursor-pointer hover:bg-grey-50 focus-visible:bg-grey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <Badge variant="outline" className="shrink-0">{contractDateTypeLabel(milestone.dateType)}</Badge>
          {/* Wrap rather than truncate: a nowrap title would widen the whole page at phone width */}
          <span className="text-sm font-semibold text-grey-800 min-w-0 break-words">
            {milestone.contractTitle}
          </span>
          {/* Decorative: the row itself is the link */}
          <span aria-hidden="true" className="ml-auto shrink-0 text-grey-400">
            <ExternalLink className="h-4 w-4" />
          </span>
        </div>
        <span className="text-sm text-grey-500 block break-words">
          {milestone.vendorName}
          {milestone.description ? ` · ${milestone.description}` : ''}
        </span>
      </div>
      <div className="text-right min-w-[120px]">
        <span className="text-sm text-grey-500 block">{formatCalendarDate(milestone.date)}</span>
        <Badge variant={getUrgencyVariant(milestone.daysUntil)} className="mt-1">
          {milestone.daysUntil} days
        </Badge>
      </div>
    </div>
  );
};

/**
 * CostPerUserRow Component
 *
 * One of the most expensive contracts per user (#17): title, vendor, seat count and the annual
 * value per seat. The whole row opens the contract, by pointer or keyboard, like MilestoneRow.
 */
const CostPerUserRow: React.FC<{
  rank: number;
  item: CostPerUserItem;
  onClick: (contractId: string) => void;
}> = ({ rank, item, onClick }) => {
  const open = () => onClick(item.contractId);
  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`Open ${item.contractTitle}`}
      className="flex items-center gap-4 px-4 py-3 border-b border-border last:border-b-0 cursor-pointer hover:bg-grey-50 focus-visible:bg-grey-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          open();
        }
      }}
    >
      <span className="w-6 shrink-0 text-sm text-grey-400 text-right tabular-nums">{rank}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-semibold text-grey-800 min-w-0 break-words">{item.contractTitle}</span>
          <span aria-hidden="true" className="ml-auto shrink-0 text-grey-400">
            <ExternalLink className="h-4 w-4" />
          </span>
        </div>
        <span className="text-sm text-grey-500 block break-words">
          {item.vendorName} · {item.seatCount} {item.seatCount === 1 ? 'seat' : 'seats'}
        </span>
      </div>
      <div className="text-right min-w-[120px]">
        <span className="text-sm font-semibold text-grey-800 block">{formatCostPerUser(item.costPerUser)}</span>
        <span className="text-xs text-grey-500">per user · {formatCurrency(item.annualContractValue)} a year</span>
      </div>
    </div>
  );
};

/**
 * Dashboard Page Component
 *
 * Main dashboard view displaying summary statistics, spend trends,
 * contract status distribution, upcoming renewals, upcoming milestones,
 * license utilization and the most expensive contracts per user.
 */
const Dashboard: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch dashboard data
  const {
    data: summary,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useDashboardSummary();

  const {
    data: renewals,
    isLoading: isRenewalsLoading,
    error: renewalsError,
  } = useUpcomingRenewals(90);

  const {
    data: spendTrends,
    isLoading: isSpendTrendsLoading,
    error: spendTrendsError,
  } = useSpendTrends(12);

  const {
    data: contractsByStatus,
    isLoading: isContractsByStatusLoading,
    error: contractsByStatusError,
  } = useContractsByStatus();

  const {
    data: licenseUtilization,
    isLoading: isLicenseUtilizationLoading,
    error: licenseUtilizationError,
  } = useLicenseUtilization();

  const {
    data: spendByVendor,
    isLoading: isSpendByVendorLoading,
  } = useSpendByVendor(8);

  const {
    data: spendByDepartment,
    isLoading: isSpendByDepartmentLoading,
  } = useSpendByDepartment();

  const {
    data: milestones,
    isLoading: isMilestonesLoading,
    error: milestonesError,
  } = useUpcomingMilestones(90);

  const {
    data: costPerUser,
    isLoading: isCostPerUserLoading,
    error: costPerUserError,
  } = useCostPerUser(10);

  /**
   * Handle refresh - invalidate all dashboard queries
   */
  const handleRefresh = (): void => {
    queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
  };

  /**
   * Handle navigation to contract detail
   */
  const handleContractClick = (contractId: string): void => {
    navigate({ to: `/contracts/${contractId}` });
  };

  /**
   * Render error alert with retry button
   */
  const renderError = (error: Error, onRetry: () => void): React.ReactNode => (
    <Alert variant="destructive" className="mb-4">
      <AlertTitle>Error Loading Data</AlertTitle>
      <AlertDescription>
        {error.message}
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-2 block">
          Retry
        </Button>
      </AlertDescription>
    </Alert>
  );

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-semibold text-grey-800">Dashboard</h1>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={handleRefresh}>
              <RefreshCw className="h-5 w-5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Refresh dashboard data</TooltipContent>
        </Tooltip>
      </div>

      {/* Summary Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4 mb-8">
        <SummaryCard
          title="Total Active Contracts"
          value={summary?.totalActiveContracts ?? 0}
          icon={<TrendingUp className="h-6 w-6" />}
          color="#1976d2"
          isLoading={isSummaryLoading}
        />
        <SummaryCard
          title="Total Annual Value"
          value={summary ? formatCurrency(summary.totalAnnualValue) : '$0'}
          icon={<DollarSign className="h-6 w-6" />}
          color="#2e7d32"
          isLoading={isSummaryLoading}
        />
        <SummaryCard
          title="License Compliance Rate"
          value={
            summary
              ? `${summary.complianceRate?.toFixed(1) ?? 0}%`
              : '0%'
          }
          icon={<CheckCircle className="h-6 w-6" />}
          color={
            summary && summary.complianceRate >= 95
              ? '#2e7d32'
              : summary && summary.complianceRate >= 85
              ? '#f57c00'
              : '#d32f2f'
          }
          isLoading={isSummaryLoading}
        />
        <SummaryCard
          title="Contracts Expiring This Quarter"
          value={summary?.contractsExpiringThisQuarter ?? 0}
          icon={<AlertTriangle className="h-6 w-6" />}
          color="#d32f2f"
          isLoading={isSummaryLoading}
        />
        <SummaryCard
          title="Inside Notice Window"
          value={summary?.contractsInNoticeWindow ?? 0}
          subtitle={`${formatCurrency(summary?.noticeWindowAnnualValue ?? 0)} annual value, status Pending Renewal`}
          icon={<CalendarClock className="h-6 w-6" />}
          color="#ed6c02"
          isLoading={isSummaryLoading}
          linkToStatus={ContractStatus.PendingRenewal}
          testId="notice-window-tile"
        />
      </div>

      {/* Error Display */}
      {summaryError && renderError(summaryError, handleRefresh)}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* Spend Trends Chart */}
        <Card className="lg:col-span-2 min-h-[400px]">
          <CardHeader>
            <CardTitle>Spending Trends</CardTitle>
            <CardDescription>Monthly contract spending over the past 12 months</CardDescription>
          </CardHeader>
          <CardContent>
            {isSpendTrendsLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : spendTrendsError ? (
              <Alert variant="destructive">
                <AlertDescription>Failed to load spending trends: {spendTrendsError.message}</AlertDescription>
              </Alert>
            ) : spendTrends && spendTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={spendTrends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#DFE3E8" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#919EAB" />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    stroke="#919EAB"
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <RechartsTooltip formatter={(value) => formatCurrency(Number(value))} />
                  <Line
                    type="monotone"
                    dataKey="totalSpend"
                    stroke="#00A76F"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <p className="text-sm text-grey-500">No spending data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contracts by Status Chart */}
        <Card className="min-h-[400px]">
          <CardHeader>
            <CardTitle>Contracts by Status</CardTitle>
            <CardDescription>Current contract status distribution</CardDescription>
          </CardHeader>
          <CardContent>
            {isContractsByStatusLoading ? (
              <div className="flex justify-center">
                <Skeleton className="h-[240px] w-[240px] rounded-full" />
              </div>
            ) : contractsByStatusError ? (
              <Alert variant="destructive">
                <AlertDescription>Failed to load contract status: {contractsByStatusError.message}</AlertDescription>
              </Alert>
            ) : contractsByStatus && contractsByStatus.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={contractsByStatus}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                  >
                    {contractsByStatus.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[240px]">
                <p className="text-sm text-grey-500">No contract data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Renewal Forecast (#18): its own component so this file only gains one element */}
      <RenewalForecastCard />

      {/* Department budgets (#19): its own component as well */}
      <BudgetSummaryCard />

      <DocumentReviewsCard />

      {/* Spend Analysis Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
        {/* Spend by Vendor Chart */}
        <Card className="lg:col-span-2 min-h-[400px]">
          <CardHeader>
            <CardTitle>Spend by Vendor</CardTitle>
            <CardDescription>Top vendors by annual contract value</CardDescription>
          </CardHeader>
          <CardContent>
            {isSpendByVendorLoading ? (
              <Skeleton className="h-[300px] w-full" />
            ) : spendByVendor && spendByVendor.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={spendByVendor} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#DFE3E8" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 12 }}
                    stroke="#919EAB"
                    tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`}
                  />
                  <YAxis
                    type="category"
                    dataKey="vendorName"
                    tick={{ fontSize: 12 }}
                    stroke="#919EAB"
                    width={120}
                  />
                  <RechartsTooltip
                    formatter={(value) => [formatCurrency(Number(value), { minimumFractionDigits: 2, maximumFractionDigits: 2 }), 'Annual Spend']}
                  />
                  <Bar dataKey="annualSpend" fill="#2563eb" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px]">
                <p className="text-sm text-grey-500">No vendor spend data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Spend by Department Chart */}
        <Card className="min-h-[400px]">
          <CardHeader>
            <CardTitle>Spend by Cost Center</CardTitle>
            <CardDescription>Cost record distribution by department</CardDescription>
          </CardHeader>
          <CardContent>
            {isSpendByDepartmentLoading ? (
              <div className="flex justify-center">
                <Skeleton className="h-[240px] w-[240px] rounded-full" />
              </div>
            ) : spendByDepartment && spendByDepartment.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={spendByDepartment}
                    dataKey="totalSpend"
                    nameKey="department"
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                  >
                    {spendByDepartment.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip formatter={(value) => formatCurrency(Number(value), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[240px]">
                <p className="text-sm text-grey-500">No department spend data available</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Upcoming Renewals */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Upcoming Renewals</CardTitle>
            <CardDescription>Contracts expiring in the next 90 days</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {isRenewalsLoading ? (
              <div className="p-4 space-y-2">
                <Skeleton className="h-[60px] w-full" />
                <Skeleton className="h-[60px] w-full" />
                <Skeleton className="h-[60px] w-full" />
              </div>
            ) : renewalsError ? (
              <div className="p-4">
                <Alert variant="destructive">
                  <AlertDescription>Failed to load upcoming renewals: {renewalsError.message}</AlertDescription>
                </Alert>
              </div>
            ) : renewals && renewals.length > 0 ? (
              <div className="max-h-[500px] overflow-auto">
                {renewals.map((renewal) => (
                  <RenewalRow
                    key={renewal.contractId}
                    renewal={renewal}
                    onClick={handleContractClick}
                  />
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <p className="text-sm text-grey-500">No upcoming renewals in the next 90 days</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* License Utilization Summary */}
        <Card>
          <CardHeader>
            <CardTitle>License Utilization</CardTitle>
            <CardDescription>Overall license allocation status</CardDescription>
          </CardHeader>
          <CardContent>
            {isLicenseUtilizationLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : licenseUtilizationError ? (
              <Alert variant="destructive">
                <AlertDescription>Failed to load license utilization: {licenseUtilizationError.message}</AlertDescription>
              </Alert>
            ) : licenseUtilization ? (
              <div>
                {/* Overall Progress */}
                <div className="mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="text-sm text-grey-500">Total Allocated</span>
                    <span className="text-sm font-semibold text-grey-800">
                      {licenseUtilization.totalAllocated} / {licenseUtilization.totalPurchased}
                    </span>
                  </div>
                  <Progress
                    value={Math.min(licenseUtilization.utilizationPercentage ?? 0, 100)}
                    className="h-2.5"
                    indicatorClassName={cn(
                      licenseUtilization.utilizationPercentage > 100
                        ? 'bg-destructive'
                        : licenseUtilization.utilizationPercentage > 90
                        ? 'bg-warning'
                        : 'bg-success'
                    )}
                  />
                  <span className="text-xs text-grey-500 mt-1 block">
                    {licenseUtilization.utilizationPercentage?.toFixed(1) ?? 0}% utilized
                  </span>
                </div>

                {/* Metrics */}
                <div className="flex flex-col gap-3">
                  <div className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-grey-500">Over-Allocated</span>
                      <Badge variant={licenseUtilization.overAllocatedCount > 0 ? 'destructive' : 'default'}>
                        {licenseUtilization.overAllocatedCount}
                      </Badge>
                    </div>
                  </div>

                  <div className="border border-border rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-grey-500">Under-Utilized</span>
                      <Badge variant={licenseUtilization.underUtilizedCount > 0 ? 'warning' : 'default'}>
                        {licenseUtilization.underUtilizedCount}
                      </Badge>
                    </div>
                    <span className="text-xs text-grey-500 mt-1 block">
                      Less than 50% utilization
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-grey-500">No license data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upcoming Milestones */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle>Upcoming Milestones</CardTitle>
          <CardDescription>Contract dates in the next 90 days</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isMilestonesLoading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-[60px] w-full" />
              <Skeleton className="h-[60px] w-full" />
              <Skeleton className="h-[60px] w-full" />
            </div>
          ) : milestonesError ? (
            <div className="p-4">
              <Alert variant="destructive">
                <AlertDescription>Failed to load upcoming milestones: {milestonesError.message}</AlertDescription>
              </Alert>
            </div>
          ) : milestones && milestones.length > 0 ? (
            <div className="max-h-[500px] overflow-auto">
              {milestones.map((milestone) => (
                <MilestoneRow
                  key={milestone.id}
                  milestone={milestone}
                  onClick={handleContractClick}
                />
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-sm text-grey-500">No milestones in the next 90 days</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Most Expensive per User (#17) */}
      <Card className="mt-4" data-testid="cost-per-user-card">
        <CardHeader>
          <CardTitle>Most Expensive per User</CardTitle>
          <CardDescription>Top 10 in-force contracts by annual value per seat</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isCostPerUserLoading ? (
            <div className="p-4 space-y-2">
              <Skeleton className="h-[60px] w-full" />
              <Skeleton className="h-[60px] w-full" />
              <Skeleton className="h-[60px] w-full" />
            </div>
          ) : costPerUserError ? (
            <div className="p-4">
              <Alert variant="destructive">
                <AlertDescription>Failed to load cost per user: {costPerUserError.message}</AlertDescription>
              </Alert>
            </div>
          ) : costPerUser && costPerUser.length > 0 ? (
            <div className="max-h-[500px] overflow-auto">
              {costPerUser.map((item, index) => (
                <CostPerUserRow
                  key={item.contractId}
                  rank={index + 1}
                  item={item}
                  onClick={handleContractClick}
                />
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <p className="text-sm text-grey-500">No contracts with seat data yet</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
